import React, { useEffect, useState } from 'react';
import {
  DetalheDaPesquisa,
  fetchAuditoriaCotacaoOnline,
  fetchDetalheCotacaoOnline,
  PaginaDePesquisas,
  PesquisaDoConsumo,
} from '../../../api/orcamentoCiliaApi';
import Paginacao from '../../../components/paginacao/Paginacao';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { PrecosPorCanal } from '../../orcamento_cilia/components/CotacaoOnlineSecao';
import SearchIcon from '../../../img/search-icon.svg';
import '../../recepcao/pages/Recepcao.css';
import '../pages/AuditoriaPage.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../orcamento_cilia/pages/OrcamentoCiliaDetail.css';

/**
 * Aba "Cotação online" da Auditoria (Victor, 20/10/2026): toda pesquisa de preço feita nas
 * requisições — quem pediu, a peça, quando, o desfecho e o que cada canal trouxe.
 *
 * Só leitura: o detalhe reaproveita o `PrecosPorCanal` do modal da requisição, sem o botão de
 * confirmar credenciamento.
 *
 * 🔴 Falha de carga vira faixa de erro, nunca "nenhuma cotação feita" (regra da at-60).
 */

const SITUACAO: Record<PesquisaDoConsumo['status'], string> = {
  PENDENTE: 'Na fila',
  EM_ANDAMENTO: 'Pesquisando',
  CONCLUIDA: 'Concluída',
  FALHOU: 'Falhou',
};

const ROTULO_CANAL: Record<string, string> = {
  CONCESSIONARIAS: 'Concessionárias',
  MERCADO_LIVRE: 'Mercado Livre',
  FERRO_VELHO: 'Ferro-velho',
  PARALELAS: 'Paralelas',
  LOJAS_AUTOPECAS: 'Lojas de autopeças',
};

const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const como = (p: { origem: string; nivel: string }) =>
  `${p.origem === 'REBUSCA' ? 'Rebusca' : 'Cotação da requisição'}, nível ${p.nivel === 'CUIDADOSO' ? 'cuidadoso' : 'econômico'}`;

function desfecho(p: PesquisaDoConsumo): string {
  if (p.status !== 'CONCLUIDA') return SITUACAO[p.status];
  if (!p.menorPreco) return 'Nenhum canal serviu';
  return `${moeda(p.menorPreco.preco)} (${ROTULO_CANAL[p.menorPreco.canal] ?? p.menorPreco.canal})`;
}

const COLUNAS: ColunaTabela<PesquisaDoConsumo>[] = [
  { titulo: 'Quando', largura: 140, texto: (p) => quando(p.criadaEm) },
  // Na automática, quem aparece é quem concluiu a requisição: é esse clique que dispara a pesquisa.
  { titulo: 'Quem', largura: 150, larguraMaxima: 200, texto: (p) => p.disparadaPorNome ?? 'Não registrado' },
  { titulo: 'Peça', largura: 240, larguraMaxima: 320, texto: (p) => (p.peca ? `${p.peca.codigo} ${p.peca.descricao}` : 'Peça não encontrada') },
  { titulo: 'Placa', largura: 90, texto: (p) => p.placa ?? '-' },
  { titulo: 'Como', largura: 200, texto: como },
  { titulo: 'Desfecho', largura: 200, larguraMaxima: 260, texto: desfecho },
];

const DetalheModal: React.FC<{ id: string; onFechar: () => void }> = ({ id, onFechar }) => {
  const [detalhe, setDetalhe] = useState<DetalheDaPesquisa | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    fetchDetalheCotacaoOnline(id)
      .then((d) => vivo && setDetalhe(d))
      .catch(() => vivo && setErro(true));
    return () => {
      vivo = false;
    };
  }, [id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onFechar();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onFechar]);

  return (
    <div className="auditoria-modal-overlay" role="presentation" onClick={onFechar}>
      <div
        className="auditoria-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Cotação online"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(760px, 100%)', overflow: 'hidden' }}
      >
        <div className="auditoria-modal-header">
          <h3>{detalhe?.peca?.descricao ?? 'Cotação online'}</h3>
          <button className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>
        {/*
          O corpo ROLA: `.auditoria-modal` tem altura máxima de 88vh, mas o corpo não tinha
          overflow, e os cinco canais vazavam para fora do cartão. `minHeight: 0` deixa o item
          flex encolher abaixo do conteúdo, que é o que faz o scroll existir.
        */}
        <div className="auditoria-modal-body" style={{ overflowY: 'auto', minHeight: 0, gap: 8 }}>
          {erro ? (
            <p className="erro-de-tela">Não foi possível abrir esta pesquisa. Recarregue a página; se persistir, acione a TI.</p>
          ) : !detalhe ? (
            <p className="solicitacao-desfecho-hint">Carregando...</p>
          ) : (
            <>
              <p className="solicitacao-desfecho-hint">
                {[
                  detalhe.peca ? `Código ${detalhe.peca.codigo}` : null,
                  detalhe.placa ? `Placa ${detalhe.placa}` : null,
                  detalhe.numeroCilia ? `Cilia ${detalhe.numeroCilia}` : null,
                ]
                  .filter(Boolean)
                  .join(' | ')}
              </p>
              <p className="solicitacao-desfecho-hint">
                Pedida em {quando(detalhe.criadaEm)}
                {detalhe.disparadaPorNome ? ` por ${detalhe.disparadaPorNome}` : ''} | {como(detalhe)} | {SITUACAO[detalhe.status]}
                {detalhe.concluidaEm ? ` em ${quando(detalhe.concluidaEm)}` : ''} | {detalhe.buscas} buscas | custo estimado US${' '}
                {detalhe.custoUsd.toFixed(2).replace('.', ',')}
              </p>
              {detalhe.status === 'FALHOU' && (
                <p className="erro-de-tela">A pesquisa falhou: {detalhe.erro ?? 'erro na pesquisa'}.</p>
              )}
              {detalhe.status === 'CONCLUIDA' && <PrecosPorCanal ofertas={detalhe.ofertas} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AuditoriaCotacaoOnline: React.FC = () => {
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState<PaginaDePesquisas | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [aberta, setAberta] = useState<string | null>(null);

  // A busca dispara meio segundo depois da última tecla: uma consulta por palavra, não por letra.
  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(buscaInput);
      setPagina(1);
    }, 500);
    return () => clearTimeout(t);
  }, [buscaInput]);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    fetchAuditoriaCotacaoOnline(busca, pagina)
      .then((r) => {
        if (!vivo) return;
        setDados(r);
        setErro(false);
      })
      .catch(() => {
        if (!vivo) return;
        setDados(null);
        setErro(true);
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [busca, pagina]);

  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / dados.porPagina)) : 1;

  return (
    // Dentro do cartão branco das outras abas (`auditoria-timeline-card`): solta no fundo cinza, a
    // tabela ficava recuada e sem moldura, diferente do resto da Auditoria.
    <div className="auditoria-timeline-card" style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', boxSizing: 'border-box' }}>
      <div className="auditoria-card-heading" style={{ marginBottom: 0 }}>
        Cotações online
      </div>
      <p className="solicitacao-desfecho-hint" style={{ margin: 0 }}>
        Toda pesquisa de preço online feita nas requisições. Clique numa linha para ver o que cada canal trouxe. Na pesquisa
        automática, quem aparece é quem concluiu a requisição, porque é esse clique que dispara a pesquisa das peças.
      </p>
      <div className="auditoria-modal-search-wrapper">
        <img className="search-icon-inside" src={SearchIcon} alt="" width="18" height="18" />
        <input
          className="auditoria-modal-search"
          type="text"
          placeholder="Buscar por placa, peça ou quem pediu..."
          value={buscaInput}
          onChange={(e) => setBuscaInput(e.target.value)}
          aria-label="Buscar cotação online"
        />
      </div>

      {erro ? (
        <p className="erro-de-tela" role="alert">
          Não foi possível carregar as cotações online. Recarregue a página; se persistir, acione a TI.
        </p>
      ) : (
        <>
          <TabelaSistema
            colunas={COLUNAS}
            itens={dados?.itens ?? []}
            chave={(p) => p.id}
            vazio={busca.trim() ? 'Nenhuma cotação online encontrada para esta busca.' : 'Nenhuma cotação online feita até agora.'}
            carregando={carregando && !dados}
            onClickLinha={(p) => setAberta(p.id)}
          />
          {dados && dados.total > dados.porPagina && (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          )}
        </>
      )}

      {aberta && <DetalheModal id={aberta} onFechar={() => setAberta(null)} />}
    </div>
  );
};

export default AuditoriaCotacaoOnline;
